export interface NoteEvent {
  midiNote: number;
  startMs: number;
  durationMs: number;
  velocity: number;
  channel: number;
  track: number;
}

export interface TempoEvent {
  tickPosition: number;
  microsecondsPerQuarter: number;
  bpm: number;
}

export interface TimeSigEvent {
  tickPosition: number;
  numerator: number;
  denominatorPower: number;
  ticksPerQuarter: number;
}

export interface ParsedMidi {
  events: NoteEvent[];
  tempoMap: TempoEvent[];
  timeSigMap: TimeSigEvent[];
}

interface TempoEntry {
  tick: number;
  tempoUs: number;
}

interface TimeSigEntry {
  tick: number;
  numerator: number;
  denominatorPower: number;
}

interface TickNote {
  midiNote: number;
  startTick: number;
  endTick: number;
  velocity: number;
  channel: number;
  track: number;
}

interface PendingNote {
  midiNote: number;
  channel: number;
  track: number;
  velocity: number;
  startTick: number;
}

function readVarLen(view: DataView, offset: number): { value: number; bytesRead: number } {
  let value = 0;
  let bytesRead = 0;
  let byte: number;
  do {
    byte = view.getUint8(offset + bytesRead);
    value = (value << 7) | (byte & 0x7f);
    bytesRead++;
  } while ((byte & 0x80) !== 0);
  return { value, bytesRead };
}

function tickToMs(tick: number, tempoMap: readonly TempoEntry[], ppq: number): number {
  let ms = 0;
  let prevTick = 0;
  let tempoUs = 500000;

  for (const entry of tempoMap) {
    if (entry.tick >= tick) break;
    ms += ((entry.tick - prevTick) * tempoUs) / ppq / 1000;
    prevTick = entry.tick;
    tempoUs = entry.tempoUs;
  }

  ms += ((tick - prevTick) * tempoUs) / ppq / 1000;
  return ms;
}

function readUint24(view: DataView, offset: number): number {
  return (view.getUint8(offset) << 16) | (view.getUint8(offset + 1) << 8) | view.getUint8(offset + 2);
}

function closePending(pending: Map<string, PendingNote>, key: string, endTick: number, notes: TickNote[]): void {
  const p = pending.get(key);
  if (!p) return;
  notes.push({
    midiNote: p.midiNote,
    startTick: p.startTick,
    endTick,
    velocity: p.velocity,
    channel: p.channel,
    track: p.track,
  });
  pending.delete(key);
}

function parseTrack(
  view: DataView,
  trackStart: number,
  trackLength: number,
  trackIndex: number,
  tempoMap: TempoEntry[],
  timeSigEntries: TimeSigEntry[],
  collectTempo: boolean,
): TickNote[] {
  const notes: TickNote[] = [];
  const pending = new Map<string, PendingNote>();
  let offset = trackStart;
  const trackEnd = trackStart + trackLength;
  let tick = 0;
  let runningStatus = 0;

  while (offset < trackEnd) {
    const delta = readVarLen(view, offset);
    offset += delta.bytesRead;
    tick += delta.value;

    if (offset >= trackEnd) break;

    let statusByte = view.getUint8(offset);

    if (statusByte < 0x80) {
      statusByte = runningStatus;
    } else {
      offset++;
    }

    if (statusByte === 0xff) {
      const metaType = view.getUint8(offset);
      offset++;
      const len = readVarLen(view, offset);
      offset += len.bytesRead;

      if (metaType === 0x51 && len.value === 3 && collectTempo) {
        tempoMap.push({ tick, tempoUs: readUint24(view, offset) });
      }

      if (metaType === 0x58 && len.value === 4 && collectTempo) {
        timeSigEntries.push({
          tick,
          numerator: view.getUint8(offset),
          denominatorPower: view.getUint8(offset + 1),
        });
      }

      if (metaType === 0x2f) {
        break;
      }

      offset += len.value;
      continue;
    }

    if (statusByte === 0xf0 || statusByte === 0xf7) {
      const len = readVarLen(view, offset);
      offset += len.bytesRead + len.value;
      continue;
    }

    if ((statusByte & 0xf0) === 0xf0) {
      switch (statusByte) {
        case 0xf1: offset += 1; break;
        case 0xf2: offset += 2; break;
        case 0xf3: offset += 1; break;
        default: break;
      }
      continue;
    }
    runningStatus = statusByte;
    const msgType = statusByte & 0xf0;
    const channel = statusByte & 0x0f;

    switch (msgType) {
      case 0x80: {
        const note = view.getUint8(offset);
        offset += 2;
        closePending(pending, `${channel}-${note}`, tick, notes);
        break;
      }

      case 0x90: {
        const note = view.getUint8(offset);
        const vel = view.getUint8(offset + 1);
        offset += 2;
        const key = `${channel}-${note}`;

        if (vel === 0) {
          closePending(pending, key, tick, notes);
        } else {
          closePending(pending, key, tick, notes);
          pending.set(key, { midiNote: note, channel, track: trackIndex, velocity: vel, startTick: tick });
        }
        break;
      }

      case 0xa0:
      case 0xb0:
      case 0xe0:
        offset += 2;
        break;

      case 0xc0:
      case 0xd0:
        offset += 1;
        break;

      default:
        break;
    }
  }
  for (const [key] of pending) {
    closePending(pending, key, tick, notes);
  }

  return notes;
}

export function parseMidi(buffer: ArrayBuffer): ParsedMidi {
  const view = new DataView(buffer);

  const headerTag =
    String.fromCharCode(view.getUint8(0)) +
    String.fromCharCode(view.getUint8(1)) +
    String.fromCharCode(view.getUint8(2)) +
    String.fromCharCode(view.getUint8(3));

  if (headerTag !== 'MThd') {
    throw new Error('Not a valid MIDI file: missing MThd header');
  }

  const headerLength = view.getUint32(4, false);
  const format = view.getUint16(8, false);
  const ntrks = view.getUint16(10, false);
  const division = view.getUint16(12, false);

  if (format === 2) throw new Error('MIDI Type 2 not supported');
  if (format !== 0 && format !== 1) throw new Error(`Unknown MIDI format: ${format}`);
  if ((division & 0x8000) !== 0) throw new Error('SMPTE time division not supported');

  const ppq = division;
  const tempoMap: TempoEntry[] = [];
  const timeSigEntries: TimeSigEntry[] = [];
  const allTickNotes: TickNote[] = [];
  let offset = 8 + headerLength;

  for (let t = 0; t < ntrks; t++) {
    const trkTag =
      String.fromCharCode(view.getUint8(offset)) +
      String.fromCharCode(view.getUint8(offset + 1)) +
      String.fromCharCode(view.getUint8(offset + 2)) +
      String.fromCharCode(view.getUint8(offset + 3));

    if (trkTag !== 'MTrk') throw new Error(`Expected MTrk at offset ${offset}, got "${trkTag}"`);

    const trackLength = view.getUint32(offset + 4, false);
    const trackDataStart = offset + 8;

    const collectTempo = format === 0 || t === 0;
    allTickNotes.push(...parseTrack(view, trackDataStart, trackLength, t, tempoMap, timeSigEntries, collectTempo));
    offset = trackDataStart + trackLength;
  }

  tempoMap.sort((a, b) => a.tick - b.tick);
  timeSigEntries.sort((a, b) => a.tick - b.tick);

  if (timeSigEntries.length === 0) {
    timeSigEntries.push({ tick: 0, numerator: 4, denominatorPower: 2 });
  }

  const events: NoteEvent[] = [];
  for (const tn of allTickNotes) {
    if (tn.channel === 9) continue;

    const startMs = tickToMs(tn.startTick, tempoMap, ppq);
    const endMs = tickToMs(tn.endTick, tempoMap, ppq);
    events.push({
      midiNote: tn.midiNote,
      startMs,
      durationMs: Math.max(0, endMs - startMs),
      velocity: tn.velocity,
      channel: tn.channel,
      track: tn.track,
    });
  }

  events.sort((a, b) => a.startMs - b.startMs);

  const exportedTempoMap: TempoEvent[] = tempoMap.map(e => ({
    tickPosition: e.tick,
    microsecondsPerQuarter: e.tempoUs,
    bpm: 60_000_000 / e.tempoUs,
  }));

  if (exportedTempoMap.length === 0) {
    exportedTempoMap.push({ tickPosition: 0, microsecondsPerQuarter: 500000, bpm: 120 });
  }

  const exportedTimeSigMap: TimeSigEvent[] = timeSigEntries.map(e => ({
    tickPosition: e.tick,
    numerator: e.numerator,
    denominatorPower: e.denominatorPower,
    ticksPerQuarter: ppq,
  }));

  return { events, tempoMap: exportedTempoMap, timeSigMap: exportedTimeSigMap };
}
